import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

// CSS Imports
import "./index.css";
import "@xyflow/react/dist/style.css";

// MODULES Import
import modules from "./modules.json";

// SECCTORS Import
import Sectors from "./Sectors/Sectors";

// SVG Imports
import BackToTop from "../../../../assets/landingPage/TopArrow.svg";

// MUI Imports
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// COMPONENTS Imports
import Loader from "../../../../components/Loader/Index";
import SVGComponent from "../../../../components/_utils/SVGComponent";
import Footer from "../../../../components/LandingPage/Footer";
import SingleSelectDropdownWithIcon from "../../../../components/_utils/CommonDropdown/SingleSelectDropdownWithIcon/index";

import { ReactFlow, useNodesState, useEdgesState } from "@xyflow/react";
import { SmartStepEdge as OriginalSmartStepEdge } from "@tisoap/react-flow-smart-edge";

import ProductCard from "./ProductCard";
import DummyProductCard from "./DummyProductCard";
import ICONS from "../../../../assets/Icons";

const countries = [
  {
    label: "India",
    icon: "https://q2pay.in/new_assets/common/countries/india.svg",
  },
  // { value: "USA", label: "United States", flag: India },
  // { value: "China", label: "China", flag: India },
  // { value: "Germany", label: "Germany", flag: India },
  // { value: "Japan", label: "Japan", flag: India },
];

const Product = () => {
  const NODE_WIDTH = 314;
  const NODE_HEIGHT = 238;
  const GAP = 50;

  // useRef
  const wrapperRef = useRef(null);

  // useState
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(countries[0]);
  const [animationUp, setAnimationUp] = useState(false);
  const topRef = useRef(null);
  const [topHeight, setTopHeight] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [isVisible, setIsVisible] = useState(false);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [flowHeight, setFlowHeight] = useState(1000);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ---- Animated edge with gradient and circles ----
  const AnimatedSmartStepEdge = React.memo((props) => {
    const containerRef = useRef(null);
    const circleStartRef = useRef(null);
    const circleEndRef = useRef(null);

    useEffect(() => {
      const g = containerRef.current;
      if (!g) return;
      const path = g.querySelector("path");
      if (!path) return;

      const strokeWidth = 6;

      // --- Circles ---
      if (!circleStartRef.current) {
        const startCircle = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "circle",
        );
        startCircle.setAttribute("r", strokeWidth * 1.5);
        startCircle.style.opacity = 0;
        startCircle.style.transition = "opacity 0.6s ease-out";
        g.appendChild(startCircle);
        circleStartRef.current = startCircle;
      }

      if (!circleEndRef.current) {
        const endCircle = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "circle",
        );
        endCircle.setAttribute("r", strokeWidth * 1.5);
        endCircle.style.opacity = 0;
        endCircle.style.transition = "opacity 0.6s ease-out";
        g.appendChild(endCircle);
        circleEndRef.current = endCircle;
      }

      const circleStart = circleStartRef.current;
      const circleEnd = circleEndRef.current;

      // --- Gradient ---
      const gradientId = `grad-${props.id}`;
      const svg = g.closest("svg");
      if (svg) {
        let defs = svg.querySelector("defs");
        if (!defs) {
          defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
          svg.insertBefore(defs, svg.firstChild);
        }

        let linearGradient = svg.querySelector(`#${gradientId}`);
        if (!linearGradient) {
          linearGradient = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "linearGradient",
          );
          linearGradient.setAttribute("id", gradientId);
          linearGradient.setAttribute("gradientUnits", "userSpaceOnUse");
          linearGradient.setAttribute("x1", props.sourceX);
          linearGradient.setAttribute("y1", props.sourceY);
          linearGradient.setAttribute("x2", props.targetX);
          linearGradient.setAttribute("y2", props.targetY);

          const stop1 = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "stop",
          );
          stop1.setAttribute("offset", "0%");
          stop1.setAttribute("stop-color", props.style?.stroke || "#EA2DFB");

          const stop2 = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "stop",
          );
          stop2.setAttribute("offset", "100%");
          stop2.setAttribute("stop-color", props.style?.stroke2 || "#00FBE2");

          linearGradient.appendChild(stop1);
          linearGradient.appendChild(stop2);
          defs.appendChild(linearGradient);
        }

        path.style.stroke = `url(#${gradientId})`;
        circleStart.setAttribute("fill", `url(#${gradientId})`);
        circleEnd.setAttribute("fill", `url(#${gradientId})`);
      }

      // --- Animate line and circles together ---
      const len = path.getTotalLength();

      path.style.strokeDasharray = len;
      path.style.strokeDashoffset = len;
      path.style.opacity = "0";

      circleStart.style.opacity = "0";
      circleEnd.style.opacity = "0";

      const startPoint = path.getPointAtLength(0);
      circleStart.setAttribute("cx", startPoint.x);
      circleStart.setAttribute("cy", startPoint.y);

      const duration = 1200; // 1.2s animation

      // --- delay start ---
      const timeout = setTimeout(() => {
        let startTime = null;

        function animate(timestamp) {
          if (!startTime) startTime = timestamp;
          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // update line
          path.style.strokeDashoffset = len * (1 - progress);
          path.style.opacity = 1;

          // update end circle position
          const endPoint = path.getPointAtLength(len * progress);
          circleEnd.setAttribute("cx", endPoint.x);
          circleEnd.setAttribute("cy", endPoint.y);

          // fade in circles
          circleStart.style.opacity = 1;
          circleEnd.style.opacity = 1;

          if (progress < 1) requestAnimationFrame(animate);
        }

        requestAnimationFrame(animate);
      }, 400);

      return () => clearTimeout(timeout);
    }, [props]);

    return (
      <g ref={containerRef}>
        <OriginalSmartStepEdge
          {...props}
          style={{
            ...(props.style || {}),
            opacity: 0,
            strokeDasharray: "1000",
            strokeDashoffset: "1000",
          }}
        />
      </g>
    );
  });

  // ---- Custom Node ----
  const CustomNode = React.memo(
    ({ data }) => {
      const w = data.width || NODE_WIDTH;
      const h = data.height || NODE_HEIGHT;

      return (
        <div
          style={{
            width: w,
            height: h,
            // transition: "width 5s linear, height 5s linear",
            display: "flex",
            alignItems: "stretch",
            justifyContent: "stretch",
          }}
        >
          <ProductCard
            key={data.id}
            moduleName={data.name}
            productName={data.feature}
            itemName={data.logo}
            descriptions={
              data?.descriptions
                .filter((x) => x.industry === selectedSector?.id)[0]
                ?.text.filter((m) => m.module === data?.id)[0]?.content
            }
            moduleId={data.id}
            width={w}
            height={h}
            expanded={data.expanded}
            type={data.type}
            edges={edges} // edges can be removed if not used in ProductCard
            logo={data.logo}
          />
        </div>
      );
    },
    (prevProps, nextProps) => {
      // Only re-render if relevant data changed
      return (
        prevProps.data.id === nextProps.data.id &&
        prevProps.data.expanded === nextProps.data.expanded &&
        prevProps.data.width === nextProps.data.width &&
        prevProps.data.height === nextProps.data.height &&
        prevProps.data.type === nextProps.data.type
      );
    },
  );

  const HeadingNode = React.memo(({ data }) => (
    <div
      style={{
        color: "#f9f9f9",
        fontWeight: "normal",
        fontSize: 20,
        textAlign: "left",
        marginTop: 20,

        minHeight: 40, // ensures at least one line space
        wordWrap: "break-word", // break long words if needed
        whiteSpace: "normal", // allow wrapping
        // maxWidth: "90%", // keeps text inside viewport
        maxWidth: isMobile ? "90%" : "",
      }}
    >
      {data.label}
    </div>
  ));

  const NODES_PER_GROUP = 4;

  const generateNodesWithHeadings = () => {
    const nodesWithHeadings = [];

    for (let i = 0; i < modules?.suites.length; i += 1) {
      // Add heading node
      const headingNode = {
        id: `heading-${i / NODES_PER_GROUP}`,
        position: { x: 0, y: 0 }, // will set in layout
        data: { label: `${modules.suites[i].name}` },
        type: "headingNode",
        draggable: false,
        selectable: false,
      };
      nodesWithHeadings.push(headingNode);

      // Add the next 4 nodes
      const groupNodes = modules?.modules
        .filter((x) => x.suite === modules?.suites[i].id)
        .map((mod) => ({
          id: `${mod.name}`,
          position: { x: 0, y: 0 },
          data: {
            ...mod,
            id: mod.id,
            name: mod.name,
            feature: mod.feature,
            // module: modules,
            descriptions: modules.descriptions,
            expanded: false,
          },
          type: "customNode",
          draggable: false,
          selectable: false,
        }));

      nodesWithHeadings.push(...groupNodes);
    }

    return nodesWithHeadings;
  };

  // ---- Main Component ----
  const [nodes, setNodes, onNodesChange] = useNodesState(
    generateNodesWithHeadings,
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const resetFlow = () => {
    setEdges([]);
    setNodes((prevNodes) => {
      const collapsedNodes = prevNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          expanded: false,
        },
      }));
      return calculateLayout(collapsedNodes);
    });
  };

  // ---- Layout calculation ----

  const calculateLayout = (nodesArr, columns = getColumns()) => {
    const colY = Array(columns).fill(0);
    let currentCol = 0;

    // For desktop ≥1025px, fix container width to 1024px for centering
    const isDesktop = window.innerWidth >= 1025;
    const containerWidth = isDesktop ? 1024 : window.innerWidth;

    const contentWidth = columns * NODE_WIDTH + (columns - 1) * GAP;
    const marginX = Math.max((containerWidth - contentWidth) / 2, 0);

    nodesArr.forEach((node) => {
      if (node.type === "headingNode") {
        node.position.x = marginX;
        node.position.y = Math.max(...colY);

        // --- dynamically detect heading height ---
        let headingHeight = 40; // default
        const el = document.querySelector(`[data-id="${node.id}"]`);
        if (el) {
          headingHeight = el.offsetHeight;
        }

        colY.fill(node.position.y + headingHeight + GAP);
        currentCol = 0;
      } else if (node.data.expanded) {
        if (columns >= 3) {
          node.data.width = contentWidth;
          node.data.height = NODE_HEIGHT;
          node.position.x = marginX;
        } else {
          node.data.width = Math.floor(containerWidth * 0.8);
          node.data.height = 320;
          node.position.x = Math.max((containerWidth - node.data.width) / 2, 0);
        }

        node.position.y = Math.max(...colY);
        colY.fill(node.position.y + node.data.height + GAP);
        currentCol = 0;
      } else {
        const col = currentCol % columns;
        node.data.width = NODE_WIDTH;
        node.data.height = NODE_HEIGHT;
        node.position.x = marginX + col * (NODE_WIDTH + GAP);
        node.position.y = colY[col];
        colY[col] += NODE_HEIGHT + GAP;
        currentCol += 1;
      }
    });

    setFlowHeight(Math.max(...colY));
    return nodesArr.map((n) => ({ ...n }));
  };

  // ---- Determine columns by screen size ----
  const getColumns = () => {
    const w = window.innerWidth;
    if (w >= 1024) return 3; // desktop: 3 columns (>=1024)
    if (w >= 768) return 2; // tablet: 2 columns (768 - 1023)
    return 1; // phone: 1 column (<=767)
  };

  useEffect(() => {
    let timer = null;

    const handleResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const width = wrapperRef.current?.offsetWidth || window.innerWidth;
        setContainerWidth(width);
        setIsMobile(width < 768);

        // recalc layout with current columns
        setNodes((nds) => calculateLayout(nds));
      }, 70);
    };

    handleResize(); // initial layout on mount
    window.addEventListener("resize", handleResize);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNodeClick = useCallback(
    (_, clickedNode) => {
      if (clickedNode.data.expanded) {
        resetFlow();
        return;
      }

      const currentDes = clickedNode.data.descriptions
        ?.filter((x) => x.industry === selectedSector?.id)[0]
        ?.text.filter((m) => m.module === clickedNode.data.id)[0]?.content;

      if (!currentDes) return;

      const updatedNodes = nodes.map((node) => {
        if (node.id === clickedNode.id) {
          return { ...node, data: { ...node.data, expanded: true } };
        }
        return { ...node, data: { ...node.data, expanded: false } };
      });

      const layoutedNodes = calculateLayout(updatedNodes);

      setNodes(layoutedNodes);

      const updatedEdges = (clickedNode.data.connectsTo || []).map(
        (target) => ({
          id: `${clickedNode.id}-${target.id}`,
          source: clickedNode.id,
          target: target.id,
          type: "animatedSmartStep",
          style: {
            stroke:
              clickedNode.data.type === "core"
                ? "var(--product-core-color)"
                : clickedNode.data.type === "recommended"
                  ? "var(--product-recommended-color)"
                  : clickedNode.data.type === "enhancement"
                    ? "var(--product-enhancement-color)"
                    : "",
            stroke2:
              target.type === "core"
                ? "var(--product-core-color)"
                : target.type === "recommended"
                  ? "var(--product-recommended-color)"
                  : target.type === "enhancement"
                    ? "var(--product-enhancement-color)"
                    : "",
            strokeWidth: 4,
          },
        }),
      );

      setEdges(updatedEdges);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, selectedSector],
  );

  const sorted = [...modules.industries].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const filteredIndustries = sorted.filter((industry) =>
    industry.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSectorClick = (sector) => {
    setLoading(true);
    setSelectedSector(sector);
    setSearchTerm(sector.name);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const handleStartSearch = () => {
    setSelectedSector(null);
    setAnimationUp(true);
  };

  const handleClear = () => {
    setSearchTerm("");
    setSelectedSector(false);
    resetFlow();
  };
  const nodeTypes = useMemo(
    () => ({
      customNode: CustomNode,
      headingNode: HeadingNode,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSector, edges, isMobile],
  );

  const edgeTypes = useMemo(
    () => ({
      animatedSmartStep: AnimatedSmartStepEdge,
    }),
    [AnimatedSmartStepEdge],
  );
  // inside your component
  const MemoizedFlow = useMemo(() => {
    return (
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        panOnDrag={false}
        panOnScroll={true}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        fitView={false}
        key={containerWidth}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        translateExtent={[
          [0, 0],
          [1024, flowHeight],
        ]}
        style={{
          width: "var(--width-full)",
          // minHeight: flowHeight,
          // height: flowHeight,
          height: isMobile ? "auto" : flowHeight, // fixed height on desktop
          minHeight: isMobile ? flowHeight : undefined, // minHeight for mobile
        }}
      />
    );
  }, [
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onNodeClick,
    nodeTypes,
    edgeTypes,
    containerWidth,
    flowHeight,
    isMobile,
  ]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    window.onload = function () {
      setTimeout(function () {
        window.scrollTo(0, 0);
      }, 50);
      setScrollPercent(0);
    };

    const handleScroll = () => {
      if (window.scrollY > 20 || document.documentElement.scrollTop > 20) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    if (topRef.current) {
      setTopHeight(topRef.current.offsetHeight);
    }
    const handleResize = () => {
      if (topRef.current) {
        setTopHeight(topRef.current.offsetHeight);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      let scrollTop = window.scrollY;
      let docHeight = document.documentElement.scrollHeight;
      let winHeight = window.innerHeight;
      let scrollPercentt = scrollTop / (docHeight - winHeight);
      let scrollPercentRounded = Math.round(scrollPercentt * 100);
      setScrollPercent(scrollPercentRounded);
    });
  }, [scrollPercent]);

  function CircularProgressWithLabel(props) {
    return (
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
          size="59px"
          sx={{ color: "var(--pending)" }}
          variant="determinate"
          {...props}
        />
        <Box
          sx={{
            zIndex: 999,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{ color: "text.secondary" }}
          >
            <img
              src={BackToTop}
              alt="back to top"
              width="50"
              style={{ right: "45px", position: "fixed", bottom: "34px" }}
            />
          </Typography>
        </Box>
      </Box>
    );
  }

  const CircleCancel =
    "https://q2pay.in/new_assets/common/dashboard/close_with_white_circle.svg";
  return (
    <>
      {isVisible && (
        <div
          onClick={scrollToTop}
          className="fixed bottom-[25px] right-[40px] cursor-pointer z-20"
        >
          <CircularProgressWithLabel value={scrollPercent} />
        </div>
      )}
      <div
        className="pt-[60px] min-h-screen flex flex-col justify-between"
        style={{
          background: "var(--product-background)",
          // background: "red",
          overflow: animationUp ? "hidden" : " auto",
          height: animationUp ? "var(--full-height)" : "",
        }}
      >
        {/* Top section */}
        <div
          ref={topRef}
          className="bg-custom-card w-full"
          style={{
            transform: animationUp
              ? `translateY(-${topHeight}px)`
              : "translateY(0)",
            transition: "transform 0.8s ease",
          }}
        >
          <div className="flex flex-col-reverse w-auto gap-gap-sm Qlg:w-max-content-width mx-auto py-padding-lg Qlg:flex items-center Qlg:flex-row justify-between">
            <div className="flex flex-col gap-gap-sm Qlg:gap-gap-lg w-[300px] Qlg:w-[542px]">
              <h1 className="text-text-primary font-semibold text-[18px] Qlg:text-[32px]">
                Xpentra modules
              </h1>
              <p className="text-text-primary text-font-xs Qlg:text-font-md font-normal">
                Xpentra is built on a modular architecture which allows
                enterprises to customise their experience by selecting the
                specific sets of features and capabilities that finance and
                business teams require. These add-on modules have been designed
                to address sector specific use cases.
              </p>
            </div>
            <div>
              <img
                src="https://q2pay.in/new_assets/graphics/landing_page/modules.svg"
                alt="modules"
                className="h-[356px] Qlg:h-[432px] w-[300px] Qlg:w-[400px] object-contain"
              />
            </div>
          </div>
        </div>
        {/* Top section */}

        {/* Section that slides */}
        <div
          style={{
            transform: animationUp
              ? `translateY(-${topHeight}px)`
              : "translateY(0)",
            transition: "transform 0.8s ease",
            background: "var(--product-background)",
          }}
        >
          <div
            className={`
              flex flex-col p-padding-md gap-gap-md Qlg:gap-gap-lg items-center flex-1
              ${animationUp ? "gradient-border" : ""}
              transform transition-all duration-700 ease-in-out
              ${selectedSector ? "translate-y-[-41%] Qlg:translate-y-[-41%] pt-stepper-gap-between Qlg:pt-padding-xs Qsm:pt-14" : "translate-y-0"}
              `}
            style={{
              background: animationUp
                ? "linear-gradient(to right, #F9F9F919, #F9F9F900)"
                : "var(--product-background)",
              borderBottomLeftRadius: animationUp ? "20px" : "0px",
              borderBottomRightRadius: animationUp ? "20px" : "0px",
              zIndex: 1,
            }}
          >
            <div className="flex flex-col gap-gap-xs items-center ">
              <h1 className="font-semibold text-font-md Qlg:text-[28px] text-text-primary text-center">
                Choose from one of{" "}
                <span className="text-[20px] Qlg:text-[36px] text-pending">
                  40+
                </span>{" "}
                sectors
              </h1>
              <p className="text-text-secondary text-font-xs text-center font-normal">
                Discover sector-specific modules and the benefits they bring.
              </p>
              {animationUp && (
                <div
                  className="absolute top-[15px] right-[15px] md:top-[20px] md:right-[30px] Qlg:top-[30px] Qlg:right-[30px] w-[22px] h-[22px] Qlg:w-[30px] Qlg:h-[30px] bg-[#344E7C] flex justify-center items-center rounded-full cursor-pointer hover:bg-hover-bg-white-light"
                  onClick={() => {
                    setAnimationUp(false);
                    setSelectedSector(null);
                  }}
                >
                  <img
                    src={ICONS?.CLOSE}
                    alt="reject"
                    style={{
                      width: "10px",
                      height: "10px",
                    }}
                  />
                </div>
              )}
            </div>
            {animationUp ? (
              <>
                <div className="flex flex-col Qlg:flex-row gap-gap-sm">
                  {/* Search  */}
                  <div className="w-full max-w-[429px] relative mx-auto text-text-primary z-z-index-max">
                    <div className="absolute left-3 self-center -translate-y-1/2 w-[14px] h-[14px] text-text-primary z-20 fade-in-right">
                      <SVGComponent
                        url="https://q2pay.in/new_assets/common/view_all/search_white.svg"
                        color="var(--text-primary)"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Search by sector"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        handleStartSearch();
                        resetFlow();
                      }}
                      className="w-[300px] h-[36px] Qlg:w-[429px] Qlg:h-[44px] p-3 pl-padding-xl rounded-full bg-primary-nav-act font-normal text-text-secondary text-font-xs fade-in-right pr-padding-xl"
                    />
                    {searchTerm && (
                      <img
                        onClick={handleClear}
                        className="w-icon-sm h-icon-sm absolute top-[10px] Qlg:top-[15px] right-[15px] cursor-pointer"
                        src={CircleCancel}
                        alt="search-close"
                      />
                    )}
                  </div>
                  {/* Search  */}

                  {/* Dropdown + TrafficLight */}
                  <div className="flex flex-row gap-gap-sm fade-in-left">
                    <div className="Qlg:w-country-dropdown-width w-full">
                      <SingleSelectDropdownWithIcon
                        options={countries}
                        selected={selected}
                        setSelected={setSelected}
                        type="big"
                        isOpen={false}
                        showSearch={true}
                        placeholder="Select country"
                        searchPlaceholder="Search country"
                        disabled={false}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <button
                className={`px-padding-sm py-[12.5px] w-[300px] Qlg:w-[429px] rounded-[20px] bg-text-primary text-primary-bg text-font-xs Qlg:text-font-sm font-semibold hover:bg-hover-bg-white-dark ${
                  animationUp ? "fade-out-right " : ""
                }`}
                onClick={() => setAnimationUp(true)}
              >
                Search by sectors
              </button>
            )}
            {animationUp && (
              <div className="flex flex-row gap-x-gap-xl gap-y-gap-md text-text-primary text-font-md flex-wrap justify-center">
                <div className="flex flex-row gap-gap-md items-center">
                  <div className="w-icon-md h-icon-md bg-[#ea2dfb] rounded-full"></div>
                  <div>Core</div>
                </div>
                <div className="flex flex-row gap-gap-md items-center">
                  <div className="w-icon-md h-icon-md bg-[#00fbe2] rounded-full"></div>
                  <div>Recommended</div>
                </div>
                <div className="flex flex-row gap-gap-md items-center">
                  <div className="w-icon-md h-icon-md bg-[#00b7ff] rounded-full"></div>
                  <div>Enhancement</div>
                </div>
              </div>
            )}
          </div>

          {animationUp && !selectedSector ? (
            <div
              className="relative flex h-screen w-full justify-center animate-all overflow-y-auto overflow-x-hidden pb-10"
              style={{
                background: "var(--product-background)",
              }}
            >
              <div className="absolute flex flex-col gap-14 h-full w-full self-start justify-start z-z-index-base pt-padding-xl">
                <div className="flex flex-col items-center Qlg:flex-row gap-10 Qlg:justify-center">
                  <DummyProductCard />
                  <DummyProductCard />
                  <DummyProductCard />
                </div>
                <div className="hidden Qlg:flex flex-row gap-10 justify-center">
                  <DummyProductCard />
                  <DummyProductCard />
                  <DummyProductCard />
                </div>
                <div className="hidden Qlg:flex flex-row gap-10 justify-center">
                  <DummyProductCard />
                  <DummyProductCard />
                  <DummyProductCard />
                </div>

                <div className="absolute inset-0 w-full h-full bg-black/40 backdrop-blur-sm pointer-events-none"></div>
              </div>

              <div className="flex flex-wrap gap-gap-md Qmd:h-[fit-content] h-[calc(100vh-340px)] pb-padding-lg overflow-y-auto Qmd:overflow-y-hidden w-[900px] animate-all relative z-z-index-max justify-center pt-padding-lg">
                {filteredIndustries.map((industry) => (
                  <div
                    key={industry.id + searchTerm}
                    onClick={() => {
                      setLoading(true);
                      handleSectorClick(industry);
                    }}
                    className="animate-all"
                  >
                    <Sectors name={industry.name} />
                  </div>
                ))}
              </div>
            </div>
          ) : loading ? (
            <div
              className="flex justify-center items-center  "
              style={{ height: "calc(100vh - 200px)" }}
            >
              <Loader />
            </div>
          ) : selectedSector ? (
            <div
              className={`flex justify-center transition-all duration-700 ${
                selectedSector
                  ? "translate-y-[-75px] Qlg:translate-y-[-95px]"
                  : "translate-y-0"
              }`}
              style={{
                width: "100%",
                height: "100%", // fixed viewport height
                background: "var(--product-background)",
                transition:
                  "background 0.7s ease, border-radius 0.7s ease, transform 0.7s ease",
              }}
            >
              <div
                ref={wrapperRef}
                style={{
                  width: "100%", // fixed canvas width
                  height: isMobile
                    ? "calc(100vh - 290px)"
                    : "calc(100vh - 150px)", // dynamic height based on nodes
                  overflowY: "auto",
                  overflowX: "hidden",
                  WebkitOverflowScrolling: "touch", // important for smooth mobile scrolling
                  touchAction: "pan-y", // ensure vertical scroll works
                }}
              >
                {MemoizedFlow}
              </div>
            </div>
          ) : null}
        </div>
        {/* Section that slides */}
        {!animationUp && <Footer />}
      </div>
    </>
  );
};

export default Product;
